import React, { useState, useEffect } from 'react';
import { Card, Typography, Space, Tag, Button, Select, DatePicker, Empty, Spin, message, Row, Col, Divider } from 'antd';
import { PlusOutlined, CalendarOutlined, UserOutlined, CarOutlined } from '@ant-design/icons';
import { appointmentApi, Appointment } from '../../services/appointmentApi';
import { AppointmentModal } from './AppointmentModal';
import axios from 'axios';
import { getTokens } from '../../services/authApi';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;

interface User {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
}

interface DayViewProps {
  initialDate?: Dayjs;
  technicianId?: number;
}

export const DayView: React.FC<DayViewProps> = ({ initialDate, technicianId }) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs>(initialDate || dayjs());
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<number | 'all'>(technicianId || 'all');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [technicians, setTechnicians] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  useEffect(() => {
    loadTechnicians();
  }, []);

  useEffect(() => {
    loadAppointments();
  }, [selectedDate, selectedTechnicianId]);

  const loadTechnicians = async () => {
    try {
      // Get auth token
      const tokens = getTokens();
      const headers = tokens?.accessToken ? {
        Authorization: `Bearer ${tokens.accessToken}`
      } : {};

      // Fetch both technicians and owners
      const [techniciansResponse, ownersResponse] = await Promise.all([
        axios.get('/api/users', {
          params: { role: 'technician', status: 'active' },
          headers
        }),
        axios.get('/api/users', {
          params: { role: 'owner', status: 'active' },
          headers
        })
      ]);

      // Combine and sort by name
      const allUsers = [...techniciansResponse.data, ...ownersResponse.data].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setTechnicians(allUsers);
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  };

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const startOfDay = selectedDate.startOf('day').format('YYYY-MM-DD');
      const endOfDay = selectedDate.endOf('day').format('YYYY-MM-DD');

      const params: any = {
        start_date: `${startOfDay}T00:00:00`,
        end_date: `${endOfDay}T23:59:59`
      };

      if (selectedTechnicianId !== 'all') {
        params.technician_id = selectedTechnicianId;
      }

      const data = await appointmentApi.getAll(params);

      // Sort by start time
      const sorted = data.sort((a, b) =>
        dayjs(a.start_time).unix() - dayjs(b.start_time).unix()
      );

      setAppointments(sorted);
    } catch (error) {
      console.error('Error loading appointments:', error);
      message.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setModalVisible(true);
  };

  const handleCreateAppointment = () => {
    setSelectedAppointment(null);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedAppointment(null);
    loadAppointments();
  };

  // Generate time slots for the day (8 AM to 6 PM)
  const generateTimeSlots = () => {
    const slots = [];
    const startHour = 8;
    const endHour = 18;

    for (let hour = startHour; hour <= endHour; hour++) {
      slots.push({
        time: selectedDate.hour(hour).minute(0).second(0),
        label: dayjs().hour(hour).minute(0).format('h:mm A')
      });
    }

    return slots;
  };

  const getAppointmentsForTechnician = (techId: number) => {
    return appointments.filter(apt => apt.technician_id === techId);
  };

  const getAppointmentPosition = (appointment: Appointment) => {
    const startTime = dayjs(appointment.start_time);
    const startHour = startTime.hour();
    const startMinute = startTime.minute();

    // Calculate position as percentage from 8 AM
    const baseHour = 8;
    const totalMinutes = (startHour - baseHour) * 60 + startMinute;
    const dayMinutes = 10 * 60; // 10 hours (8 AM to 6 PM)

    return (totalMinutes / dayMinutes) * 100;
  };

  const getAppointmentHeight = (appointment: Appointment) => {
    const durationMinutes = appointment.duration_minutes;
    const dayMinutes = 10 * 60; // 10 hours
    return (durationMinutes / dayMinutes) * 100;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return '#52c41a';
      case 'in_progress':
        return '#1890ff';
      case 'confirmed':
        return '#13c2c2';
      case 'scheduled':
        return '#faad14';
      case 'cancelled':
        return '#ff4d4f';
      case 'no_show':
        return '#d9d9d9';
      default:
        return '#d9d9d9';
    }
  };

  const timeSlots = generateTimeSlots();
  const displayTechnicians = selectedTechnicianId === 'all'
    ? technicians
    : technicians.filter(t => t.id === selectedTechnicianId);

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <Title level={2}>
            <CalendarOutlined /> Day View - {selectedDate.format('MMMM DD, YYYY')}
          </Title>
          <Text type="secondary">
            {selectedDate.format('dddd')} - View and manage appointments
          </Text>
        </div>
        <Space>
          <DatePicker
            value={selectedDate}
            onChange={(date) => date && setSelectedDate(date)}
            format="MMM DD, YYYY"
          />
          <Select
            style={{ width: 250 }}
            value={selectedTechnicianId}
            onChange={setSelectedTechnicianId}
            placeholder="Filter by employee"
            showSearch
            filterOption={(input, option) => {
              const label = `${option?.label || ''}`.toLowerCase();
              return label.includes(input.toLowerCase());
            }}
            options={[
              { value: 'all', label: 'All Employees' },
              ...technicians.map(tech => ({
                value: tech.id,
                label: `${tech.first_name} ${tech.last_name} (${tech.role === 'owner' ? 'Owner' : 'Technician'})`
              }))
            ]}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateAppointment}
          >
            New Appointment
          </Button>
        </Space>
      </div>

      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">Total Appointments</Text>
              <Title level={2} style={{ margin: '8px 0' }}>{appointments.length}</Title>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">Completed</Text>
              <Title level={2} style={{ margin: '8px 0', color: '#52c41a' }}>
                {appointments.filter(a => a.status === 'completed').length}
              </Title>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">In Progress</Text>
              <Title level={2} style={{ margin: '8px 0', color: '#1890ff' }}>
                {appointments.filter(a => a.status === 'in_progress').length}
              </Title>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">Scheduled</Text>
              <Title level={2} style={{ margin: '8px 0', color: '#faad14' }}>
                {appointments.filter(a => a.status === 'scheduled' || a.status === 'confirmed').length}
              </Title>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Timeline View */}
      <Card>
        <Spin spinning={loading}>
          {displayTechnicians.length === 0 ? (
            <Empty description="No technicians available" />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'flex', minWidth: '800px' }}>
                {/* Time Labels Column */}
                <div style={{ width: '80px', flexShrink: 0 }}>
                  <div style={{ height: '50px', borderBottom: '1px solid #f0f0f0' }}>
                    <Text strong>Time</Text>
                  </div>
                  {timeSlots.map(slot => (
                    <div
                      key={slot.label}
                      style={{
                        height: '60px',
                        borderBottom: '1px solid #f0f0f0',
                        paddingTop: '4px'
                      }}
                    >
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {slot.label}
                      </Text>
                    </div>
                  ))}
                </div>

                {/* Technician Columns */}
                {displayTechnicians.map(technician => {
                  const techAppointments = getAppointmentsForTechnician(technician.id);

                  return (
                    <div
                      key={technician.id}
                      style={{
                        flex: 1,
                        minWidth: '200px',
                        borderLeft: '1px solid #f0f0f0',
                        position: 'relative'
                      }}
                    >
                      {/* Technician Header */}
                      <div
                        style={{
                          height: '50px',
                          borderBottom: '1px solid #f0f0f0',
                          padding: '8px',
                          backgroundColor: '#fafafa'
                        }}
                      >
                        <Space direction="vertical" size={0}>
                          <Text strong>
                            <UserOutlined /> {technician.first_name} {technician.last_name}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {techAppointments.length} appointment(s)
                          </Text>
                        </Space>
                      </div>

                      {/* Time Grid */}
                      <div style={{ position: 'relative', height: `${timeSlots.length * 60}px` }}>
                        {timeSlots.map((_, index) => (
                          <div
                            key={index}
                            style={{
                              height: '60px',
                              borderBottom: '1px solid #f0f0f0'
                            }}
                          />
                        ))}

                        {/* Appointments */}
                        {techAppointments.map(appointment => {
                          const top = getAppointmentPosition(appointment);
                          const height = getAppointmentHeight(appointment);
                          const color = getStatusColor(appointment.status);

                          return (
                            <div
                              key={appointment.id}
                              onClick={() => handleAppointmentClick(appointment)}
                              style={{
                                position: 'absolute',
                                top: `${top}%`,
                                left: '4px',
                                right: '4px',
                                height: `${Math.max(height, 5)}%`,
                                backgroundColor: color,
                                color: '#fff',
                                borderRadius: '4px',
                                padding: '8px',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                transition: 'all 0.2s',
                                zIndex: 1
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.02)';
                                e.currentTarget.style.zIndex = '10';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.zIndex = '1';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                              }}
                            >
                              <div style={{ fontSize: '11px', fontWeight: 'bold' }}>
                                {dayjs(appointment.start_time).format('h:mm A')}
                              </div>
                              <div style={{ fontSize: '12px', marginTop: '2px' }}>
                                {appointment.title}
                              </div>
                              {appointment.customer && (
                                <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.9 }}>
                                  {appointment.customer.first_name} {appointment.customer.last_name}
                                </div>
                              )}
                              {(appointment.has_conflict || appointment.waiting_on_parts) && (
                                <div style={{ marginTop: '4px' }}>
                                  {appointment.has_conflict && !appointment.conflict_acknowledged && (
                                    <Tag color="red" style={{ fontSize: '10px', padding: '0 4px' }}>!</Tag>
                                  )}
                                  {appointment.waiting_on_parts && (
                                    <Tag color="orange" style={{ fontSize: '10px', padding: '0 4px' }}>P</Tag>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Spin>
      </Card>

      {/* Legend */}
      <Card style={{ marginTop: '16px' }}>
        <Space wrap>
          <Text strong>Status Legend:</Text>
          <Tag color="#52c41a">Completed</Tag>
          <Tag color="#1890ff">In Progress</Tag>
          <Tag color="#13c2c2">Confirmed</Tag>
          <Tag color="#faad14">Scheduled</Tag>
          <Tag color="#ff4d4f">Cancelled</Tag>
          <Tag color="#d9d9d9">No Show</Tag>
          <Divider type="vertical" />
          <Text strong>Indicators:</Text>
          <Tag color="red">! Conflict</Tag>
          <Tag color="orange">P Waiting on Parts</Tag>
        </Space>
      </Card>

      {/* Appointment Modal */}
      <AppointmentModal
        visible={modalVisible}
        appointment={selectedAppointment}
        onClose={handleModalClose}
        defaultDate={selectedDate}
        defaultTechnicianId={selectedTechnicianId !== 'all' ? selectedTechnicianId : undefined}
      />
    </div>
  );
};
